import { useState, useEffect, useMemo, useCallback } from "react";
import { useLocation, Outlet, useNavigate } from "react-router-dom";
import { Suspense } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNetworkStatus } from "@/hooks/useNetworkStatus";
import { supabase } from "@/lib/supabase";
import { useSEO } from "@/hooks/useSEO";
import { useAdminBadgeCounts, type AdminBadgeCounts } from "@/hooks/useAdminBadgeCounts";

// --- Sidebar Nav Structure ---
interface NavItemDef {
  path: string;
  label: string;
  icon: string;
}

interface NavSection {
  title: string;
  items: NavItemDef[];
}

const navSections: NavSection[] = [
  {
    title: "Main",
    items: [
      { path: "/dashboard", label: "Dashboard", icon: "ri-dashboard-line" },
      { path: "/submitted-forms", label: "Submitted Forms", icon: "ri-inbox-archive-line" },
      { path: "/orders", label: "Orders", icon: "ri-shopping-cart-2-line" },
      { path: "/store", label: "Store", icon: "ri-store-2-line" },
      { path: "/store/settings", label: "Store Settings", icon: "ri-settings-3-line" },
      { path: "/blog", label: "Blog", icon: "ri-article-line" },
    ],
  },
  {
    title: "Content",
    items: [
      { path: "/media", label: "Media Library", icon: "ri-image-line" },
      { path: "/announcements", label: "Announcements", icon: "ri-megaphone-line" },
      { path: "/forms", label: "Form Collection", icon: "ri-survey-line" },
    ],
  },
  {
    title: "Settings",
    items: [
      { path: "/settings/seo", label: "SEO Settings", icon: "ri-search-line" },
      { path: "/settings/domain", label: "Domain", icon: "ri-global-line" },
      { path: "/settings/integrations", label: "Integrations", icon: "ri-plug-line" },
      { path: "/settings/payments", label: "Payments", icon: "ri-secure-payment-line" },
      { path: "/settings/delivery", label: "Delivery", icon: "ri-truck-line" },
      { path: "/settings/billing", label: "Billing", icon: "ri-bill-line" },
      { path: "/settings/social", label: "Social Media", icon: "ri-share-line" },
      { path: "/settings/users", label: "Users", icon: "ri-user-settings-line" },
      { path: "/settings/webhooks", label: "Webhooks", icon: "ri-webhook-line" },
      { path: "/settings/api-keys", label: "API Keys", icon: "ri-key-2-line" },
    ],
  },
  {
    title: "Analytics & Backup",
    items: [
      { path: "/analytics", label: "Analytics", icon: "ri-bar-chart-box-line" },
      { path: "/seo-analysis", label: "SEO Analysis", icon: "ri-seo-line" },
      { path: "/system-status", label: "System Status", icon: "ri-server-line" },
      { path: "/export", label: "Export Data", icon: "ri-download-2-line" },
      { path: "/import", label: "Import Data", icon: "ri-upload-2-line" },
      { path: "/backups", label: "Backups", icon: "ri-database-2-line" },
      { path: "/cron", label: "Cron Jobs", icon: "ri-time-line" },
      { path: "/backend-export", label: "Backend Export", icon: "ri-code-box-line" },
      { path: "/migrate", label: "Migrate Data", icon: "ri-swap-line" },
      { path: "/compare", label: "Compare", icon: "ri-git-pull-request-line" },
      { path: "/reset", label: "Reset Site", icon: "ri-restart-line" },
    ],
  },
  {
    title: "Builder",
    items: [
      { path: "/web-editor", label: "Web Editor", icon: "ri-hammer-line" },
    ],
  },
];

function isActiveRoute(current: string, target: string): boolean {
  if (current === target) return true;
  if (target !== "/" && current.startsWith(target + "/")) return true;
  return false;
}

function SidebarItem({
  item,
  collapsed,
  active,
  badge,
  onNavigate,
}: {
  item: NavItemDef;
  collapsed: boolean;
  active: boolean;
  badge?: number;
  onNavigate: () => void;
}) {
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      onNavigate();
    },
    [onNavigate]
  );

  return (
    <a
      href={item.path}
      onClick={handleClick}
      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors text-sm cursor-pointer select-none ${
        active
          ? "bg-teal-50 text-teal-700 font-semibold"
          : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
      } ${collapsed ? "justify-center" : ""}`}
      title={collapsed ? item.label : ""}
    >
      <i className={`${item.icon} text-base w-5 h-5 flex items-center justify-center`}></i>
      {!collapsed && (
        <>
          <span className="whitespace-nowrap">{item.label}</span>
          {badge !== undefined && badge > 0 && (
            <span className="ml-auto bg-rose-500 text-white text-[10px] font-bold min-w-[18px] h-[18px] flex items-center justify-center rounded-full px-1 shrink-0">
              {badge > 99 ? "99+" : badge}
            </span>
          )}
        </>
      )}
    </a>
  );
}

function AdminSidebar({
  collapsed,
  onToggle,
  badgeCounts,
}: {
  collapsed: boolean;
  onToggle: () => void;
  badgeCounts: AdminBadgeCounts;
}) {
  const location = useLocation();
  const navigate = useNavigate();

  const navTree = useMemo(() => {
    return navSections.map((section) => (
      <div key={section.title} className="mb-2">
        <p
          className={`px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-gray-400 ${
            collapsed ? "text-center" : ""
          }`}
        >
          {collapsed ? section.title.charAt(0) : section.title}
        </p>
        {section.items.map((item) => {
          const active = isActiveRoute(location.pathname, item.path);
          const badge = item.path === "/orders" ? badgeCounts.total : undefined;
          return (
            <SidebarItem
              key={item.path}
              item={item}
              collapsed={collapsed}
              active={active}
              badge={badge}
              onNavigate={() => navigate(item.path)}
            />
          );
        })}
      </div>
    ));
  }, [location.pathname, collapsed, badgeCounts, navigate]);

  return (
    <aside
      className={`border-r border-gray-200 bg-white flex flex-col shrink-0 transition-all duration-300 ${
        collapsed ? "w-14" : "w-56"
      }`}
    >
      <div className="flex items-center justify-between px-3 py-3 border-b border-gray-100 h-12">
        {!collapsed && (
          <div className="flex items-center gap-2">
            <img
              src="https://storage.readdy-site.link/project_files/2c592e0a-ac16-4b41-a5a3-753cedde2d0c/822e93fe-7f15-40fb-a3ea-010140fb5a30_isman-falcon-figma2.png?v=92077e07eca8bef5c11686db6fc155dd"
              alt="ISMAN"
              className="h-6 w-auto object-contain"
            />
            <span className="text-xs font-bold text-teal-800">ISMAN</span>
          </div>
        )}
        <button
          onClick={onToggle}
          className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-gray-100 text-gray-500 cursor-pointer"
          title={collapsed ? "Expand" : "Collapse"}
        >
          <i className={collapsed ? "ri-arrow-right-s-line" : "ri-arrow-left-s-line"}></i>
        </button>
      </div>

      <div className="flex-1 overflow-y-auto py-2 px-1">{navTree}</div>
    </aside>
  );
}

export default function AdminLayout({ children }: { children?: React.ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [isOnline, setIsOnline] = useState(true);
  const [isServingCache, setIsServingCache] = useState(false);

  const { status } = useNetworkStatus();
  const badgeCounts = useAdminBadgeCounts();

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.servingCache) setIsServingCache(true);
      if (detail?.fresh) setIsServingCache(false);
    };
    window.addEventListener("cache-status-change", handler);
    return () => window.removeEventListener("cache-status-change", handler);
  }, []);

  const handleLogout = useCallback(async () => {
    await logout();
    navigate("/login", { replace: true });
  }, [logout, navigate]);

  const handleForceRefresh = useCallback(async () => {
    supabase.removeAllChannels();
    window.dispatchEvent(new CustomEvent("force-cache-refresh"));
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.controller?.postMessage({ type: "FORCE_REFRESH" });
    }
    setIsServingCache(false);
    window.location.reload();
  }, []);

  useSEO({
    robots: "noindex, nofollow",
  });

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Offline / cache banner */}
      {(!isOnline || isServingCache) && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 text-xs text-amber-700 flex items-center justify-between z-50">
          <span className="flex items-center gap-1.5">
            <i className="ri-wifi-off-line"></i>
            {!isOnline
              ? "You are offline. Serving cached data."
              : "Serving cached data. Realtime is paused."}
          </span>
          <button
            onClick={handleForceRefresh}
            className="text-amber-800 font-semibold hover:underline cursor-pointer"
          >
            Refresh Now
          </button>
        </div>
      )}

      {/* Top bar */}
      <header className="bg-white border-b border-gray-200 h-12 flex items-center justify-between px-4 shrink-0 z-40">
        <div className="flex items-center gap-3">
          <a
            href="/"
            className="text-teal-700 hover:text-teal-800 text-sm font-semibold cursor-pointer whitespace-nowrap flex items-center gap-1.5 px-2.5 py-1 rounded-md hover:bg-teal-50 transition-colors"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Site
          </a>
          <a
            href="/dashboard"
            onClick={(e) => {
              e.preventDefault();
              navigate("/dashboard");
            }}
            className="text-gray-600 hover:text-teal-700 text-sm font-semibold cursor-pointer whitespace-nowrap flex items-center gap-1.5 px-2.5 py-1 rounded-md hover:bg-gray-50 transition-colors relative"
          >
            <i className="ri-dashboard-line"></i>
            Dashboard
            {!badgeCounts.loading && badgeCounts.total > 0 && (
              <span className="absolute -top-1 -right-2 bg-rose-500 text-white text-[10px] font-bold min-w-[18px] h-[18px] flex items-center justify-center rounded-full px-1 shadow-sm">
                {badgeCounts.total > 99 ? "99+" : badgeCounts.total}
              </span>
            )}
          </a>
        </div>
        <div className="flex items-center gap-4">
          {/* Network status */}
          <div className="flex items-center gap-1.5 text-xs">
            <span
              className={`w-2 h-2 rounded-full ${
                isOnline && !isServingCache
                  ? "bg-emerald-500 animate-pulse"
                  : isOnline
                  ? "bg-amber-500"
                  : "bg-rose-500"
              }`}
            ></span>
            <span className="text-gray-500 hidden sm:inline">
              {isOnline && !isServingCache ? "Live" : isOnline ? "Cached" : "Offline"}
            </span>
          </div>

          {status.effectiveType && (
            <span className="text-[10px] text-gray-400 uppercase tracking-wider hidden sm:inline">
              {status.effectiveType}
            </span>
          )}

          <span className="text-xs text-gray-600 hidden sm:inline">
            {user?.email}
          </span>
          <button
            onClick={handleLogout}
            className="text-xs text-rose-600 hover:text-rose-700 font-medium cursor-pointer whitespace-nowrap flex items-center gap-1"
          >
            <i className="ri-logout-box-r-line"></i> Logout
          </button>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        <AdminSidebar
          collapsed={collapsed}
          onToggle={() => setCollapsed(!collapsed)}
          badgeCounts={badgeCounts}
        />
        <main className="flex-1 overflow-y-auto p-4 md:p-6 relative">
          {/* New boundary per page: the click shows a loader instantly instead of waiting on the old page */}
          <Suspense key={location.pathname} fallback={<PageLoading />}>
            {children ?? <Outlet />}
          </Suspense>
        </main>
      </div>

      {/* Force Refresh FAB */}
      <button
        onClick={handleForceRefresh}
        className={`fixed bottom-6 right-6 w-12 h-12 rounded-full shadow-lg flex items-center justify-center text-white z-50 transition-colors cursor-pointer ${
          isOnline && !isServingCache
            ? "bg-teal-600 hover:bg-teal-500"
            : "bg-amber-500 hover:bg-amber-400"
        }`}
        title="Force Refresh"
      >
        <i className="ri-refresh-line text-lg"></i>
      </button>
    </div>
  );
}

function PageLoading() {
  return (
    <div className="flex items-center justify-center py-24 text-gray-400 text-sm gap-2">
      <i className="ri-loader-4-line animate-spin text-xl text-teal-600"></i> Opening…
    </div>
  );
}
